import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, BookOpen, ShoppingCart, FileText, Download, ArrowLeft, Star, Bookmark, ExternalLink, Heart } from 'lucide-react';
import { WishlistButton } from '../components/products/WishlistButton';
import { ReviewSection } from '../components/products/ReviewSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EbookDetail = () => {
  const { id } = useParams();
  const [ebook, setEbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchased, setPurchased] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ebookRes] = await Promise.all([
          axios.get(`${API}/ebooks/${id}`),
        ]);
        setEbook(ebookRes.data);

        // Check if purchased
        if (isAuthenticated) {
          try {
            const myEbooks = await axios.get(`${API}/my-ebooks`);
            const owned = myEbooks.data.some(e => e.id === id);
            setPurchased(owned);
          } catch (err) {
            // Not fatal
          }
        }
      } catch (err) {
        console.error('Failed to load e-book:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isAuthenticated]);

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to add items to cart');
      return;
    }
    setAddingToCart(true);
    try {
      await addToCart({
        product_id: ebook.id,
        ebook_id: ebook.id,
        name: ebook.name,
        price: ebook.price,
        image_url: ebook.cover_url,
        is_digital: true,
        quantity: 1,
      });
      toast.success(`"${ebook.name}" added to cart`);
    } catch (err) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/ebooks/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Download failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = ebook.original_filename || `${ebook.name}.${ebook.file_format || 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Download started!');
    } catch (err) {
      toast.error(err.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!ebook) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <BookOpen className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
        <h2 className="font-serif text-xl font-bold mb-2">E-book not found</h2>
        <Link to="/ebooks"><Button variant="outline">Back to E-books</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="ebook-detail-page">
      <Link to="/ebooks" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="h-4 w-4" />
        Back to E-books
      </Link>

      <div className="grid md:grid-cols-[300px_1fr] gap-8">
        {/* Cover */}
        <div>
          <div className="relative aspect-[3/4] bg-gradient-to-b from-muted to-muted/50 rounded-lg overflow-hidden shadow-lg">
            {ebook.cover_url ? (
              <img src={ebook.cover_url} alt={ebook.name} className="w-full h-full object-cover" />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <BookOpen className="h-20 w-20 mb-4" />
                <span className="text-lg font-medium uppercase tracking-wider">{ebook.file_format?.toUpperCase()}</span>
              </div>
            )}
            {ebook.is_featured && (
              <Badge className="absolute top-4 left-4 bg-amber-500 text-white" data-testid="featured-badge">
                <Star className="h-3 w-3 fill-current mr-1" />
                Featured
              </Badge>
            )}
          </div>
        </div>

        {/* Details */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline">{ebook.file_format?.toUpperCase()}</Badge>
            <Badge variant="outline" className="capitalize">{ebook.category}</Badge>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-2" data-testid="ebook-detail-title">
            {ebook.name}
          </h1>

          {ebook.author && (
            <p className="text-lg text-muted-foreground mb-4">by {ebook.author}</p>
          )}

          <p className="font-bold text-3xl text-primary mb-6" data-testid="ebook-detail-price">
            ${ebook.price?.toFixed(2)}
          </p>

          {/* Metadata */}
          <div className="flex flex-wrap gap-4 mb-6 text-sm text-muted-foreground">
            {ebook.pages > 0 && (
              <div className="flex items-center gap-1.5">
                <Bookmark className="h-4 w-4" />
                {ebook.pages} pages
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <FileText className="h-4 w-4" />
              {ebook.file_format?.toUpperCase()} format
            </div>
            {ebook.file_size > 0 && (
              <div className="flex items-center gap-1.5">
                <Download className="h-4 w-4" />
                {formatSize(ebook.file_size)}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            {ebook.affiliate_url ? (
              <a href={ebook.affiliate_url} target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="affiliate-link-btn"
                >
                  <ExternalLink className="h-5 w-5 mr-2" />
                  Buy from Partner — ${ebook.price?.toFixed(2)}
                </Button>
              </a>
            ) : purchased ? (
              <Button
                size="lg"
                className="btn-accent"
                onClick={handleDownload}
                disabled={downloading}
                data-testid="download-ebook-btn"
              >
                {downloading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <Download className="h-5 w-5 mr-2" />
                )}
                {downloading ? 'Downloading...' : 'Download E-book'}
              </Button>
            ) : (
              <Button
                size="lg"
                className="btn-accent"
                onClick={handleAddToCart}
                disabled={addingToCart}
                data-testid="add-to-cart-btn"
              >
                {addingToCart ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <ShoppingCart className="h-5 w-5 mr-2" />
                )}
                Add to Cart — ${ebook.price?.toFixed(2)}
              </Button>
            )}
            
            {/* Wishlist Button */}
            <WishlistButton 
              itemId={ebook.id} 
              itemType="ebook" 
              variant="outline"
              size="lg"
              showText
              className="w-full sm:w-auto"
            />
          </div>

          {/* Info card */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Download className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-sm text-foreground">Instant Digital Download</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    After purchase, you can download this {ebook.file_format?.toUpperCase()} directly from your account.
                    No shipping required — start reading immediately.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Description */}
          {ebook.description && (
            <div className="mt-8">
              <h2 className="font-serif text-xl font-bold text-foreground mb-3">About this {ebook.category === 'guide' ? 'Guide' : 'E-book'}</h2>
              <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed" data-testid="ebook-detail-description">
                {ebook.description}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Reviews Section */}
      <ReviewSection itemId={id} itemType="ebook" />
    </div>
  );
};

export default EbookDetail;
